import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, CreditCard, FileText, X } from "lucide-react";
import {
  billingBannerFor,
  cardLabel,
  formatDay,
  formatMoney,
  useBillingStatus,
  usePayStatement,
  useSaveCard,
  type BillingStatementSummary,
  type BillingStatus,
} from "../../lib/billing";

const DISMISS_KEY = "weco-billing-banner-dismissed";

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(statementId: string): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, statementId);
  } catch {
    /* storage unavailable: dismissed for this render only */
  }
}

/**
 * The bill banner at the top of the shell, for the people who can pay it.
 *
 * Owners, Admins and super admins see it when a bill is ready, when it is
 * overdue (with the date the workspace will be paused) and in a trial's last
 * week. Members never see it — `can_pay` is false for them and the server
 * sends them no amounts. A ready bill can be put away for the session; an
 * overdue one cannot.
 */
export function BillingBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: status } = useBillingStatus();
  const pay = usePayStatement();
  const saveCard = useSaveCard();
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);
  const [showBill, setShowBill] = useState(false);

  const model = billingBannerFor(status);
  if (!model || !status) return null;
  const statement = status.statement;
  if (model.dismissible && statement && dismissed === statement.id) return null;

  const locale = i18n.language;
  const amount = statement ? formatMoney(statement.total, statement.currency, locale) : "";
  const danger = model.kind === "overdue";
  // A card on file pays a ready bill (and a trial's first) by itself: the
  // banner says so instead of asking for payment.
  const card = status.card_saving ? status.card : null;
  const cardPays = !!card && model.kind !== "overdue";
  // The pause date only means something while the platform enforces it.
  const showRestriction = model.kind === "overdue" && status.enforcement_enabled && !!status.restrict_at;

  let title: string;
  let body: string;
  if (model.kind === "bill_ready") {
    title = t("billing.banner.billReadyTitle", { amount });
    body = card
      ? t("billing.banner.billReadyBodyCard", { card: cardLabel(card), date: formatDay(status.due_at, locale) })
      : t("billing.banner.billReadyBody", { date: formatDay(status.due_at, locale) });
  } else if (model.kind === "overdue") {
    title = statement ? t("billing.banner.overdueTitle", { amount }) : t("billing.banner.overdueTitleNoBill");
    body = showRestriction
      ? t("billing.banner.overdueBodyRestrict", { date: formatDay(status.restrict_at, locale) })
      : t("billing.banner.overdueBody", { date: formatDay(status.due_at, locale) });
  } else {
    title = t("billing.banner.trialTitle", { date: formatDay(status.due_at, locale) });
    body = card
      ? t("billing.banner.trialBodyCard", { card: cardLabel(card) })
      : statement
        ? t("billing.banner.trialBodyBill", { amount })
        : t("billing.banner.trialBody");
  }
  const showDaysLeft = status.days_left != null && (model.kind !== "overdue" || showRestriction);

  return (
    <>
      <div
        role={danger ? "alert" : "status"}
        data-testid="billing-banner"
        data-kind={model.kind}
        className={
          "shrink-0 flex items-center gap-3 flex-wrap px-4 py-2.5 border-b " +
          (danger
            ? "bg-red-500/10 border-red-500/30 text-red-800 dark:text-red-200"
            : "bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-100")
        }
      >
        {danger ? (
          <AlertTriangle size={18} className="shrink-0" aria-hidden />
        ) : (
          <CalendarClock size={18} className="shrink-0" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold leading-snug">
            {title}
            {showDaysLeft && (
              <span className="ms-2 font-semibold opacity-80">
                · {t("billing.banner.daysLeft", { count: status.days_left ?? 0 })}
              </span>
            )}
          </p>
          <p className="text-[12.5px] leading-snug opacity-90">{body}</p>
          {statement && !status.payment_enabled && (
            <p className="text-[12px] leading-snug opacity-80 mt-0.5">{t("billing.paymentUnavailableNote")}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {statement ? (
            <>
              <button
                type="button"
                onClick={() => setShowBill(true)}
                className="h-8 px-3 rounded-xl text-[12.5px] font-bold border border-current/30 hover:bg-foreground/5 inline-flex items-center gap-1.5"
              >
                <FileText size={14} aria-hidden />
                {t("billing.viewBill")}
              </button>
              {model.kind === "overdue" && status.card_saving && (
                <button
                  type="button"
                  onClick={() => saveCard.mutate()}
                  disabled={saveCard.isPending}
                  className="h-8 px-3 rounded-xl text-[12.5px] font-bold border border-current/30 hover:bg-foreground/5 disabled:opacity-60"
                >
                  {saveCard.isPending
                    ? t("billing.card.opening")
                    : card
                      ? t("billing.card.replace")
                      : t("billing.card.add")}
                </button>
              )}
              {status.payment_enabled && !cardPays && (
                <button
                  type="button"
                  onClick={() => pay.mutate(statement.id)}
                  disabled={pay.isPending}
                  className="h-8 px-3 rounded-xl text-[12.5px] font-bold bg-primary text-primary-foreground hover:brightness-95 disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  <CreditCard size={14} aria-hidden />
                  {pay.isPending ? t("billing.openingCheckout") : t("billing.payNow")}
                </button>
              )}
            </>
          ) : model.kind === "trial" && status.organization_id && !card ? (
            <button
              type="button"
              onClick={() => navigate(`/people/organizations/${status.organization_id}/billing/upgrade`)}
              className="h-8 px-3 rounded-xl text-[12.5px] font-bold bg-primary text-primary-foreground hover:brightness-95"
            >
              {t("billing.choosePackage")}
            </button>
          ) : null}
          {model.dismissible && statement && (
            <button
              type="button"
              aria-label={t("billing.banner.dismiss")}
              title={t("billing.banner.dismiss")}
              onClick={() => {
                writeDismissed(statement.id);
                setDismissed(statement.id);
              }}
              className="w-8 h-8 rounded-xl inline-flex items-center justify-center hover:bg-foreground/5"
            >
              <X size={15} aria-hidden />
            </button>
          )}
        </div>
      </div>
      {showBill && statement && (
        <BillDialog
          status={status}
          statement={statement}
          onClose={() => setShowBill(false)}
          onPay={() => pay.mutate(statement.id)}
          paying={pay.isPending}
        />
      )}
    </>
  );
}

/** The bill itself: what it covers and what it costs, as the server issued it. */
export function BillDialog({
  status,
  statement,
  onClose,
  onPay,
  paying,
}: {
  status: BillingStatus;
  statement: BillingStatementSummary;
  onClose: () => void;
  onPay: () => void;
  paying: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows: Array<[string, string]> = [
    [t("billing.bill.package"), statement.plan_name],
    [
      t("billing.bill.period"),
      `${formatDay(statement.period_start, locale)} – ${formatDay(statement.period_end, locale)}`,
    ],
    [
      t("billing.bill.billing"),
      statement.billing_period === "yearly" ? t("billing.bill.yearly") : t("billing.bill.monthly"),
    ],
    [t("billing.bill.users"), String(statement.seats)],
  ];
  if (status.due_at) rows.push([t("billing.bill.due"), formatDay(status.due_at, locale)]);

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("billing.bill.title")}
        className="w-full max-w-md rounded-3xl border border-border bg-background text-foreground p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-[17px] font-black">{t("billing.bill.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("billing.bill.close")}
            className="w-8 h-8 rounded-xl inline-flex items-center justify-center hover:bg-foreground/5"
          >
            <X size={15} aria-hidden />
          </button>
        </div>
        <dl className="space-y-2 text-[13px]">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-semibold text-end">{value}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-3 pt-2 mt-2 border-t border-border">
            <dt className="font-bold">{t("billing.bill.total")}</dt>
            <dd className="font-black text-end" data-testid="bill-total">
              {formatMoney(statement.total, statement.currency, locale)}
            </dd>
          </div>
        </dl>
        {status.outstanding.length > 1 && (
          <p className="text-[12px] text-muted-foreground mt-3">
            {t("billing.bill.moreOutstanding", { count: status.outstanding.length - 1 })}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-2xl text-[13px] font-bold border border-border hover:bg-muted"
          >
            {t("billing.bill.close")}
          </button>
          {status.payment_enabled && (
            <button
              type="button"
              onClick={onPay}
              disabled={paying}
              className="h-9 px-4 rounded-2xl text-[13px] font-bold bg-primary text-primary-foreground hover:brightness-95 disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              <CreditCard size={15} aria-hidden />
              {paying ? t("billing.openingCheckout") : t("billing.payNow")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
